import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";

const COMPANY_NAME = "NetBasis International";

const title = "Formation et coaching";
const heroImage = "https://net-basis.net/Bilder/ict-training-coaching-dach-market-entry.webp";
const heroAlt = "Un homme d'affaires visualise vente, leadership et compétences interculturelles sur le marché DACH.";
const heroClaimLine1 = "Guidé vers\nl'objectif !";
const benefitsTitle = "Comment vous profitez de notre expertise en __formation et coaching__ :";
const p1Text = "Qu'il s'agisse de la définition générale de la stratégie et des structures pour dynamiser et diriger une telle activité, de la focalisation sur des descriptions de projet clairement définies avec des jalons convenus, des calculs de coûts et le « Return on Investment », ou de toutes les activités liées à la vente et au marketing – un projet d'entrée sur le marché s'accompagne de nombreux thèmes essentiels et fondamentaux qui exigent une rigueur et un traitement appropriés si vous souhaitez que ce plan devienne un succès.";
const p2Text = "Cela devient encore plus difficile lorsque tout doit être réalisé pour un marché étranger, dans une autre langue, dans un autre environnement et au sein d'une autre culture.";
const p3Text = "Si, en tant que constructeur, vous décidez néanmoins de tenter et de mener à bien cette entrée sur le marché de manière autonome et avec votre propre personnel, NetBasis International peut tout de même vous apporter son soutien et vous accompagner globalement ou dans des domaines spécifiques par des formations ou des coachings individuels. Ce faisant, nous mettons délibérément l'accent sur le transfert de connaissances et de compétences en collaborant avec des collaborateurs individuels ou des équipes entières de votre entreprise, dans le sens de votre objectif. Notre ambition envers nous-mêmes est d'agir avec toute la flexibilité requise, de fournir une qualité exceptionnelle et de devenir, au fil du temps, votre conseiller de confiance.";
const bottomClaim = "NetBasis International - drive to excellence with excellent drivers !";

const benefits = [
  "Réunions préparatoires pour la définition précise des objectifs de formation et de coaching",
  "Définition d'une vision claire pour guider en toute sécurité les prises de décision et les mesures à prendre",
  "Approche globale de tous les domaines essentiels pour le développement durable de votre activité",
  "Évaluation approfondie de la situation ainsi qu'analyse structurée des problèmes et des potentiels",
  "Accompagnement stratégique pour le développement clair de votre stratégie d'entrée sur le marché",
  "Création d'un plan d'action individuel avec des objectifs de gestion de projet mesurables",
  "Analyse axée sur le marché des produits, des solutions et de la concurrence pertinente",
  "Présentation optimale des produits et des solutions, y compris la valorisation de soi devant les décideurs",
  "Établissement du bon modèle pour nouer des relations avec les clients potentiels en vue de conclure des affaires",
  "Prospection commerciale adaptée au marché, excellent service client et fidélisation à long terme",
  "Soutien ciblé de vos collaborateurs pour rendre leur travail de projet manifestement efficace",
  "Développement durable des meilleures équipes et direction efficace des groupes et équipes",
  "Mise à disposition d'un soutien stratégique permanent pour le PDG, le conseil d'administration et la direction",
  "Réalisation de séances de coaching sur mesure avec des mesures de suivi claires",
];

export default function FormationEtCoachingFrPage() {
  const strongName = COMPANY_NAME;
  const p1Parts = p1Text.split(strongName);
  const p2Parts = p2Text.split(strongName);
  const p3Parts = p3Text.split(strongName);

  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Formation & Coaching | NetBasis International"
        description="Formations et coachings axés sur la pratique pour les équipes de vente pour le déploiement réussi des stratégies B2B."
        ogUrl="https://net-basis.net/fr/services/formation-et-coaching"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        {/* Hero image */}
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src={heroImage}
                alt={heroAlt}
                title={title}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>

              <div className="absolute inset-0 z-20 flex items-start justify-start pt-[8%] md:pt-[calc(9%-1.5cm)] lg:pt-[calc(8%-1.5cm)] pl-6 md:pl-[calc(1.5rem+1cm)] pr-4 pointer-events-none">
                <p
                  className="text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-left training-text-slide"
                  style={{
                    fontFamily: '"Open Sans", sans-serif',
                    lineHeight: '1.5',
                  }}
                >
                  {heroClaimLine1.split('\n').map((line, i) => (
                    <span key={i} className="block">{line}</span>
                  ))}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {title}
            </h2>

            <h1 className="text-foreground-950 leading-relaxed text-sm md:text-base font-semibold mb-4">
              Votre partenaire de confiance pour une formation individuelle et un coaching stratégique dans la région DACH. Nous accompagnons les constructeurs étrangers lors de leur entrée autonome sur le marché et sécurisons la réussite de vos équipes locales grâce à un transfert de compétences ciblé.
            </h1>

            <div className="space-y-4 text-foreground-950 leading-relaxed text-sm md:text-base">
              <p>
                {p1Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p1Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p2Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p2Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>
              <p>
                {p3Parts.map((part, i) => (
                  <span key={i}>
                    {part}
                    {i < p3Parts.length - 1 && (
                      <strong className="text-primary-600">{strongName}</strong>
                    )}
                  </span>
                ))}
              </p>

            </div>

            {/* Benefits heading with italic word */}
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mt-8 md:mt-10 mb-5 md:mb-7">
              {benefitsTitle.split("__").map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>

            {/* Benefits with checkmarks */}
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>

            {/* Widget 6 */}
            <p className="font-heading text-sm md:text-base text-primary-600 font-bold mt-8 md:mt-10 mb-3 md:mb-4">
              Pourquoi des cours de formation, des séminaires et du coaching d'entreprise ?
            </p>

            {/* Widget 7 */}
            <p className="text-foreground-950 leading-relaxed text-sm md:text-base">
              Votre vision de l'ouverture d'un ou de plusieurs nouveaux marchés bénéficie d'une structure parfaitement adaptée et d'une approche applicable sur le terrain. Cela garantit l'harmonie avec l'ensemble de votre culture d'entreprise et veille à ce que votre projet à l'international devienne un succès durable.
            </p>
          </div>
        </section>

        {/* Bottom claim */}
        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}